<template>
  <div class="gallery main-content wide">
    <div class="wrap">
      <sidebar-left />
      <div class="gallery-content">
        <h1>{{ title }}</h1>
        <div
          v-if="type"
          class="gallery-block"
        >
          <div
            v-for="(item, index) in showedItems"
            :key="index"
            class="gallery-box"
          >
            <div class="gallery-img-container">
              <img
                v-if="type !== 'video'"
                :src="item.thumbnail.source"
                :alt="item.thumbnail.alt"
                @click="openModal(item)"
              >
              <template v-else>
                <iframe
                  v-if="item.type === 'youtube' || item.type === 'vimeo'"
                  :src="videoSource(item)"
                  frameborder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; fullscreen;"
                  controls="0"
                />
                <video
                  v-else
                  @click="openModal(item)"
                >
                  <source
                    :src="item.video"
                    :type="item.fileType"
                  >
                </video>
              </template>
            </div>
            <div class="gallery-box-desc">
              <h3 v-if="item.title">{{ item.title }}</h3>
              <p v-if="item.fileSize">{{ $t('reusable.fileSize', { size: item.fileSize }) }}</p>
              <p v-if="item.fileType">{{ $t('reusable.fileType', { type: item.fileType }) }}</p>
              <blue-button
                class="button"
                :label="$t('download')"
                :type="'btn-ghost'"
                @clicked="download(item)"
              />
            </div>
          </div>
          <div
            v-if="showNavigation"
            class="items-count"
          >
            <div>
              {{
                $t('reusable.imagesOf', {
                  total: items.length,
                  visible: visibleItems
                })
              }}
            </div>

            <blue-button
              class="button"
              :label="$t('buttons.loadMore')"
              :type="'btn-ghost'"
              @clicked="loadMore()"
            />
          </div>
        </div>
      </div>
    </div>
    <small-loader />
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import ModalMedia from 'containers/Pages/Gallery/ModalMedia'
  import Sidebar from 'components/Sidebar/Sidebar'
  import BlueButton from 'components/Buttons/Blue'

  export default {
    components: {
      'sidebar-left': Sidebar,
      BlueButton,
      SmallLoader: () => import(/* webpackChunkName: "small-loader" */ 'components/SmallLoader')
    },
    data () {
      return {
        type: '',
        title: '',
        items: []
      }
    },
    computed: {
      ...mapState('galleryPage', [
        'visibleItems',
        'perPage',
        'sectionValues',
        'activePage'
      ]),

      showedItems () {
        if (this.visibleItems && this.items && this.items.length > 0) {
          return this.items.slice(0, this.visibleItems)
        } else {
          return []
        }
      },
      showNavigation () {
        return (
          this.items &&
          this.items.length > this.perPage &&
          this.visibleItems <= this.items.length
        )
      }
    },
    created () {
      this.type = this.activePage

      this.$store.dispatch('galleryPage/fetchSectionValues', this.type)
      this.title = this.sectionValues.title
      this.items =
        this.type !== 'video'
          ? this.sectionValues.images
          : this.sectionValues.videos
    },
    methods: {
      videoSource (item) {
        return item.video + '?autoplay=0'
      },
      loadMore () {
        this.$store.dispatch('galleryPage/setCurrentVisibleItems')
      },
      openModal (props) {
        this.$store.dispatch('main/openModal')
        this.$store.commit('main/setShowModalHeader', false)
        this.$store.commit('main/setShowModalHeaderWhite', true)
        this.$store.commit('main/setModalTitle', props.title)
        this.$store.commit('main/setModalSubTitle', '')
        this.$store.commit('main/setModalContent', ModalMedia)
        this.$store.commit('main/setModalType', 'centered')
        this.$store.commit('main/setModalClass', 'image-modal')
        this.$store.commit('main/setShowCloseIcon', false)
        this.$store.commit('main/setShowModalHeader', false)
        this.$store.commit('main/setModalCloseFn', this.closeModal)
        this.$store.commit('main/setModalProps', {
          pageType: this.type,
          source: this.type === 'video' ? props.video : props.img.source,
          fileType: props.fileType,
          alt: this.type === 'video' ? '' : props.img.alt
        })
      },
      closeModal () {
        this.$store.dispatch('main/closeModal')
      },
      download (item) {
        if (this.type !== 'video') {
          this.$store.dispatch('galleryPage/downloadImage', item )
        } else if (this.type === 'video') {
          window.open(item.video, '_blank')
        }
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import '~assets/styles/common.scss';

  .gallery {
    padding: 26px 0 0;

    @include bp-tablet-only {
      padding: 44px 0 82px;
    }

    @include bp-mf-desktop {
      padding: 40px 0 72px;
    }

    .wrap {
      display: flex;
      flex-direction: column;
      @include clearfix();

      @include bp-mf-desktop {
        display: block;
      }
    }
  }

  .gallery-content {
    padding: 0 23px;

    @include bp-tablet-only {
      padding: 0 26px;
    }

    h1 {
      margin-top: 0;
      font-size: em(32);
      font-weight: 300;
      word-wrap: break-word;

      @include bp-mf-tablet {
        margin-bottom: 26px;
        font-size: em(40);
      }
    }
  }

  .gallery-block {
    display: flex;
    flex-wrap: wrap;
  }

  .gallery-box {
    margin-bottom: 24px;
    padding: 15px 22px 12px 22px;
    width: 100%;
    border: 1px solid $grey-border;
    border-radius: 8px;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @include bp-tablet-only {
      width: calc(50% - 12px);
    }

    @include bp-mf-desktop {
      width: calc((100% / 3) - 14px);
    }

    &:not(:nth-child(3n)) {
      @include bp-mf-desktop {
        margin-right: 21px;
      }
    }

    .gallery-box-desc {
      margin-top: 10px;
      display: block;

      h3 {
        margin: 0;
        margin-bottom: 6px;
        font-size: em(14);
      }

      p {
        margin: 0 2px;
        font-size: em(10);
      }

      .button {
        margin-top: 15px;
        display: block;
      }
    }

    .gallery-img-container {
      display: flex;
      width: 100%;
      flex: 1 0 auto;
      justify-content: center;
      align-items: flex-start;
      overflow: hidden;

      img {
        width: 100%;
        cursor: pointer;
      }

      iframe,
      video {
        max-height: 100%;
        width: 100%;
        cursor: pointer;
        background-color: black;
      }
    }
  }

  .items-count {
    display: inline-block;
    margin: 0 auto;
    margin-top: 30px;
    font-size: em(12);
    text-align: center;
    color: $grey;

    @include bp-mf-tablet {
      font-size: em(14);
    }

    .button {
      margin: 10px 0 30px;
      text-align: center;
      height: 48px;
      padding: 0 16px;
      line-height: 48px;
      max-width: 100%;
    }
  }
</style>
